import { useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import { ConversationListItem } from 'Models/chat';

export const SIDEBAR_WIDTH = 280;

/** `updatedAt` is ISO 8601 from the backend; a bad value renders as nothing rather than "Invalid Date". */
function relativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNowStrict(date, { addSuffix: true });
}

// ─── One conversation row ─────────────────────────────────────────────────────

function ConversationRow({
  conversation,
  isActive,
  isBusy,
  accent,
  onSelect,
  onRequestDelete,
}: {
  conversation: ConversationListItem;
  isActive: boolean;
  isBusy: boolean;
  accent: string;
  onSelect: () => void;
  onRequestDelete: () => void;
}) {
  const when = relativeTime(conversation.updatedAt);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-current={isActive ? 'true' : undefined}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 1,
        mb: 0.5,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        bgcolor: isActive ? 'rgba(19,3,58,0.07)' : 'transparent',
        '&:hover': { bgcolor: isActive ? 'rgba(19,3,58,0.09)' : 'rgba(19,3,58,0.04)' },
        '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: '-2px' },
        // Hover reveals the delete affordance on a pointer; touch has no hover,
        // so there it stays visible.
        '& .conversation-delete': { opacity: 0, transition: 'opacity 0.15s ease' },
        '&:hover .conversation-delete': { opacity: 1 },
        '@media (hover: none)': { '& .conversation-delete': { opacity: 1 } },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: isActive ? 700 : 500,
            color: isActive ? accent : 'text.primary',
          }}
        >
          {conversation.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {when && `${when} · `}
          {conversation.messageCount} message{conversation.messageCount === 1 ? '' : 's'}
        </Typography>
      </Box>

      {isBusy ? (
        <CircularProgress size={15} thickness={5} sx={{ mr: 0.75, flexShrink: 0 }} />
      ) : (
        <Tooltip title="Delete conversation" placement="top" arrow>
          <IconButton
            className="conversation-delete"
            size="small"
            aria-label={`Delete ${conversation.title}`}
            onClick={(event) => {
              event.stopPropagation(); // the row underneath would otherwise switch conversation
              onRequestDelete();
            }}
            sx={{ flexShrink: 0, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
          >
            <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// ─── Loading and empty treatments ─────────────────────────────────────────────

function ListSkeleton() {
  return (
    <Box sx={{ px: 1.25 }}>
      {[0, 1, 2, 3].map((i) => (
        <Box key={i} sx={{ py: 1 }}>
          <Skeleton animation="wave" height={18} width={`${85 - i * 12}%`} />
          <Skeleton animation="wave" height={14} width="45%" />
        </Box>
      ))}
    </Box>
  );
}

function EmptyList() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1,
        px: 2,
        py: 5,
        color: 'text.secondary',
      }}
    >
      <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 26, opacity: 0.5 }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        No conversations yet
      </Typography>
      <Typography variant="caption">Ask a question and it will show up here.</Typography>
    </Box>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export interface ConversationSidebarProps {
  conversations: ConversationListItem[];
  isLoading: boolean;
  /** Conversation currently rendered in the message column, if any. */
  activeId: string | null;
  /** Row with a request in flight — being fetched for resume, or being deleted. */
  busyId?: string | null;
  accent: string;
  deleteError?: boolean;
  onSelect: (conversationId: string) => void;
  onNew: () => void;
  onDelete: (conversationId: string) => void;
}

/**
 * Conversation list for the assistant page (REV-201).
 *
 * Presentational: the page owns the listing query, the resume fetch and the
 * delete mutation, because it also has to react to them — swapping the rendered
 * transcript, or starting a new conversation when the active one is deleted.
 * The only state kept here is which row the confirm dialog is asking about.
 */
export default function ConversationSidebar({
  conversations,
  isLoading,
  activeId,
  busyId = null,
  accent,
  deleteError = false,
  onSelect,
  onNew,
  onDelete,
}: ConversationSidebarProps) {
  const [pendingDelete, setPendingDelete] = useState<ConversationListItem | null>(null);

  const confirmDelete = () => {
    if (pendingDelete) onDelete(pendingDelete.conversationId);
    setPendingDelete(null);
  };

  return (
    <Box
      component="nav"
      aria-label="Conversations"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: 'rgba(255,255,255,0.7)',
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={onNew}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2.5,
            py: 1,
            color: accent,
            borderColor: 'rgba(16,24,40,0.14)',
            bgcolor: 'white',
            '&:hover': { borderColor: accent, bgcolor: 'white' },
          }}
        >
          New chat
        </Button>
        {deleteError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            Couldn’t delete that conversation. Please try again.
          </Typography>
        )}
      </Box>

      <Typography
        variant="caption"
        sx={{
          px: 2,
          pb: 0.75,
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        Recent
      </Typography>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 0.75, pb: 2 }}>
        {isLoading ? (
          <ListSkeleton />
        ) : conversations.length === 0 ? (
          <EmptyList />
        ) : (
          conversations.map((conversation) => (
            <ConversationRow
              key={conversation.conversationId}
              conversation={conversation}
              isActive={conversation.conversationId === activeId}
              isBusy={conversation.conversationId === busyId}
              accent={accent}
              onSelect={() => onSelect(conversation.conversationId)}
              onRequestDelete={() => setPendingDelete(conversation)}
            />
          ))
        )}
      </Box>

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '0.95rem' }}>Delete this conversation?</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            “{pendingDelete?.title}” and every message in it will be removed. This can’t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
